import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input-one-time-password',
  template: `
    <div class="flex gap-2 mt-3">
      <input
        *ngFor="let item of tempInputArr; let i = index"
        [class]="InputOTPCustomClassName"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        [pattern]="validationPattern"
        maxlength="1"
        [disabled]="disabled"
        [value]="value[i] || ''"
        (input)="handleInputChange($event, i)"
        (keydown)="handleKeyDown($event)"
        (keyup)="handleKeyUp($event)"
        (paste)="handlePaste($event)" />
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputOneTimePasswordComponent),
      multi: true,
    },
  ],
  styles: [
    `
      .otp-input {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        width: 45px !important;
        text-align: center;
        height: 36px !important;
        border-radius: 8px !important;
        border: solid 1px #e8eaec !important;
      }
      .otp-input:focus {
        ring: 1px;
        ring-color: var(--primary);
      }
    `,
  ],
})
export class InputOneTimePasswordComponent implements ControlValueAccessor, OnInit {
  @Input() size: number = 4;
  @Input() pattern: 'numeric' | 'alphanumeric' = 'numeric';
  @Input() className: string = '';
  @Input() disabled: boolean = false;

  value: string = '';
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    this.value = new Array(this.size).fill('').join('');
  }

  get validationPattern(): string {
    return this.pattern === 'numeric' ? '[0-9]{1}' : '[0-9a-zA-Z]{1}';
  }

  get tempInputArr(): string[] {
    return new Array(this.size).fill('-');
  }

  get InputOTPCustomClassName(): string {
    return `otp-input ${this.className}`;
  }

  writeValue(value: string): void {
    // Ensure the value is a string and matches the size
    this.value = value || new Array(this.size).fill('').join('');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInputChange(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const val = input.value;

    // Validate numeric or alphanumeric based on pattern
    if (this.pattern === 'numeric' && !/^\d$/.test(val) && val !== '') {
      input.value = ''; // Invalid input, reset the field
      return;
    }
    if (this.pattern === 'alphanumeric' && !/^[0-9a-zA-Z]$/.test(val) && val !== '') {
      input.value = ''; // Invalid input, reset the field
      return;
    }

    // Update the value array
    const valueArr = this.value.split('');
    valueArr[index] = val;

    // Trim to size and update the value
    this.value = valueArr.join('').slice(0, this.size);
    this.onChange(this.value);

    // Auto-focus next input if valid input is entered
    if (val) {
      const nextSibling = input.nextElementSibling as HTMLInputElement | null;
      nextSibling?.focus();
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const form = (event.target as HTMLElement)?.closest('form');
      if (form) {
        event.preventDefault();
        (form as HTMLFormElement).requestSubmit();
      }
      return;
    }

    // Allow navigation, paste — Tab/Escape must not hit numeric checks below.
    if (
      event.key === 'Backspace' ||
      event.key === 'Tab' ||
      event.key === 'Escape' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      (event.ctrlKey && event.key === 'v')
    ) {
      return;
    }

    // Prevent invalid keys for numeric pattern
    if (this.pattern === 'numeric' && !/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Prevent invalid keys for alphanumeric pattern
    if (this.pattern === 'alphanumeric' && !/^[0-9a-zA-Z]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
  }

  handleKeyUp(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
      const prevSibling = input.previousElementSibling as HTMLInputElement | null;
      prevSibling?.focus();
      prevSibling?.setSelectionRange(0, 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      const nextSibling = input.nextElementSibling as HTMLInputElement | null;
      nextSibling?.focus();
      nextSibling?.setSelectionRange(0, 1);
      return;
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();

    const clipboardData = event.clipboardData?.getData('text') || '';
    const validCharacters =
      this.pattern === 'numeric'
        ? clipboardData.replace(/[^0-9]/g, '') // Allow only numeric
        : clipboardData.replace(/[^0-9a-zA-Z]/g, ''); // Allow alphanumeric

    const pastedValue = validCharacters.slice(0, this.size);

    this.value = pastedValue;
    this.onChange(this.value);

    const inputs = Array.from(document.querySelectorAll('input[autocomplete="one-time-code"]')) as HTMLInputElement[];
    inputs.forEach((input, index) => {
      const char = pastedValue[index];
      if (char) {
        input.value = char; // Directly access 'value' after casting
      }
    });

    const lastInput = inputs[pastedValue.length] as HTMLInputElement | null;
    lastInput?.focus();
  }
}
